/**
 * DEV-024 · Administration → Scheduling sub-hub.
 *
 * Groups the existing Scheduling-related administration surfaces so
 * operators find them together instead of scattered across the
 * Administration overview:
 *
 *   • Resource working hours — deep-links back into the existing
 *     Field Team Management journey (`/operations/administration/field-team`)
 *     where per-resource working hours are already edited. We do NOT
 *     build a second working-hours editor.
 *   • Blackout periods — the existing `OperationsBlackouts` page at
 *     `/operations/administration/blackouts`. Its URL is preserved
 *     so bookmarks and deep links keep working.
 *
 * Calendar Policies is explicitly future scope — this sub-hub does
 * NOT introduce default appointment durations, travel buffers, minimum
 * notice, new conflict handling, global working hours or any other
 * new scheduling rule/setting. Presentation + grouping only.
 *
 * Permissions are inherited from the existing surfaces:
 *   • Blackouts card — `manage_calendar`.
 *   • Working hours card — `manage_engineers` (Field Team access).
 * The Scheduling sub-hub itself is visible when the user carries at
 * least one of the two capabilities. Unauthorised children remain
 * hidden. No permission grants or removals.
 */
import React from "react";
import { Link } from "react-router-dom";
import { CalendarOff, HardHat, ArrowUpRight, Lock, Clock } from "lucide-react";
import OperationsShell from "@/components/operations/OperationsShell";
import BackButton from "@/components/BackButton";
import PortalHero from "@/components/ui/PortalHero";
import { portalIdentity } from "@/lib/portalIdentity";
import { useAuth } from "@/context/AuthContext";

const SCHEDULING_CARDS = [
    {
        key:   "working-hours",
        perm:  "manage_engineers",
        // DEV-024 correction · Working hours are edited inside the
        // existing Resource Management workspace (`/operations/resources`).
        // Deep-link straight to the Availability tab so the operator
        // lands on the resource-level working-pattern editor without
        // navigating anywhere else. NO duplicate editor built.
        to:    "/operations/resources?tab=availability",
        icon:  HardHat,
        label: "Resource working hours",
        desc:  "Open Resource Management on the Availability tab. Select a resource to edit their working pattern day-by-day (non-working, morning only, afternoon only, full day).",
        testid: "ops-scheduling-card-working-hours",
    },
    {
        key:   "blackouts",
        perm:  "manage_calendar",
        // Preserves the existing `/operations/administration/blackouts`
        // URL — bookmarks and deep links continue to resolve.
        to:    "/operations/administration/blackouts",
        icon:  CalendarOff,
        label: "Blackout periods",
        desc:  "UK bank holidays and manual blackout dates that surface as non-blocking scheduling warnings.",
        testid: "ops-scheduling-card-blackouts",
    },
];

export default function OperationsScheduling() {
    const { hasPermission, user } = useAuth();
    const visible = SCHEDULING_CARDS.filter((c) => hasPermission(c.perm));
    const identity = portalIdentity(user?.role);

    return (
        <OperationsShell>
            <div className="mx-auto max-w-6xl px-4 md:px-8 pt-6 md:pt-10 pb-16" data-testid="ops-scheduling-page">
                <BackButton
                    to="/operations/administration"
                    fallbackHref="/operations/administration"
                    label="Back to Administration"
                    testid="ops-scheduling-back"
                    className="mb-4"
                />

                <PortalHero
                    testid="ops-scheduling-hero"
                    eyebrow={`${identity.eyebrow} · SCHEDULING`}
                    title="Scheduling"
                    sentence={
                        visible.length === 0 ? (
                            <>You don't currently have access to any scheduling administration surfaces.</>
                        ) : (
                            <>
                                <strong className="text-white font-semibold">{visible.length}</strong> scheduling {visible.length === 1 ? "surface" : "surfaces"} available.
                                Manage resource working hours and blackout periods that shape day-to-day scheduling.
                            </>
                        )
                    }
                    ring={{ value: visible.length, label: visible.length === 1 ? "surface" : "surfaces" }}
                    tag="Permission-gated"
                />

                <div className="mt-8">
                    {visible.length === 0 ? (
                        <div className="rounded-xl border border-[#E5E7EB] bg-white p-8 text-center" data-testid="ops-scheduling-no-perms">
                            <Lock size={24} strokeWidth={1.5} className="mx-auto text-[#4B5565] mb-2" />
                            <div className="text-sm font-medium text-[#131B2F]">No scheduling administration permissions assigned.</div>
                            <div className="text-xs text-[#4B5565] mt-1">
                                Ask a Security Administrator to grant you Manage Calendar or Manage Engineers.
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ops-scheduling-cards">
                            {visible.map((c) => (
                                <SchedulingCard key={c.key} {...c} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </OperationsShell>
    );
}

function SchedulingCard({ to, icon: Icon, label, desc, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-[#CBD5E1] hover:shadow-md hover:-translate-y-[1px] transition-all block"
        >
            <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-[#EEF2FF] text-[#1E3A8A] flex items-center justify-center shrink-0">
                    <Icon size={18} strokeWidth={1.75} />
                </span>
                <div className="text-sm font-semibold text-[#131B2F] flex-1 min-w-0">{label}</div>
                <ArrowUpRight size={14} strokeWidth={1.75} className="text-[#4B5565]" />
            </div>
            <p className="mt-3 text-xs text-[#4B5565] leading-relaxed">{desc}</p>
        </Link>
    );
}
